import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, Pen } from 'lucide-react';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api/client';
import { getApiErrorMessage } from '@/lib/utils';
import { useSuccessPackageMutations } from '../hooks';
import type { PaymentUnit } from '../types';

const PAYMENT_UNITS: PaymentUnit[] = ['PERCENT', 'FIXED'];

const successPackageSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  minClaimAmount: z.coerce.number().min(0, 'Must be 0 or more'),
  maxClaimAmount: z.coerce.number().min(0, 'Must be 0 or more'),
  advancePayment: z.coerce.number().min(0, 'Must be 0 or more'),
  advancePaymentUnit: z.enum(['PERCENT', 'FIXED'] as const),
  successPayment: z.coerce.number().min(0, 'Must be 0 or more'),
  successPaymentUnit: z.enum(['PERCENT', 'FIXED'] as const),
});

type FormValues = z.infer<typeof successPackageSchema>;

interface SuccessPackageActionProps {
  id?: number | string;
}

export function SuccessPackageAction({ id }: SuccessPackageActionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { createMutation, updateMutation } = useSuccessPackageMutations();

  const form = useForm<FormValues>({
    resolver: zodResolver(successPackageSchema),
    defaultValues: {
      name: '',
      minClaimAmount: 0,
      maxClaimAmount: 0,
      advancePayment: 0,
      advancePaymentUnit: 'PERCENT',
      successPayment: 0,
      successPaymentUnit: 'PERCENT',
    },
  });

  const showMutation = useMutation({
    mutationFn: async (showId: number | string) => {
      return api.get(`/success-packages/${showId}`);
    },
    onSuccess: (data: Record<string, unknown>) => {
      form.reset({
        name: data.name as string ?? '',
        minClaimAmount: data.minClaimAmount as number ?? 0,
        maxClaimAmount: data.maxClaimAmount as number ?? 0,
        advancePayment: data.advancePayment as number ?? 0,
        advancePaymentUnit: (data.advancePaymentUnit as PaymentUnit) ?? 'PERCENT',
        successPayment: data.successPayment as number ?? 0,
        successPaymentUnit: (data.successPaymentUnit as PaymentUnit) ?? 'PERCENT',
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  useEffect(() => {
    if (open && id) {
      showMutation.mutate(id);
    }
    if (!open) {
      form.reset({
        name: '',
        minClaimAmount: 0,
        maxClaimAmount: 0,
        advancePayment: 0,
        advancePaymentUnit: 'PERCENT',
        successPayment: 0,
        successPaymentUnit: 'PERCENT',
      });
    }
  }, [open, id]);

  const onSubmit = (values: FormValues) => {
    if (id) {
      updateMutation.mutate(
        { id, data: values },
        { onSuccess: () => setOpen(false) }
      );
    } else {
      createMutation.mutate(values, { onSuccess: () => setOpen(false) });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {id ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen />
          </Button>
        ) : (
          <Button>
            {t('new')} <span className="lowercase">{t('successPackage')}</span>
          </Button>
        )}
      </AlertDialogTrigger>

      <AlertDialogContent className="p-6 max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {id ? t('update') : t('new')} <span className="lowercase">{t('successPackage')}</span>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label={t('closeDialog')}>
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 flex flex-col gap-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('name')} <sup className="text-red-500">*</sup></FormLabel>
                  <FormControl>
                    <Input placeholder={t('enterName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="minClaimAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('minClaimAmount')} <sup className="text-red-500">*</sup></FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxClaimAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('maxClaimAmount')} <sup className="text-red-500">*</sup></FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Advance Payment */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="advancePayment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('advancePayment')}</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="advancePaymentUnit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('unit')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('selectUnit')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PAYMENT_UNITS.map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {t(unit.toLowerCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Success Payment */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="successPayment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('successPayment')}</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="successPaymentUnit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('unit')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('selectUnit')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PAYMENT_UNITS.map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {t(unit.toLowerCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel onClick={() => setOpen(false)}>
                {t('cancel')}
              </AlertDialogCancel>
              <Button type="submit" disabled={isPending}>
                {isPending ? t('saving') : t('save')}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
